import { useTranslation } from "react-i18next";
import { Pencil, RotateCcw, RotateCw, Trash2 } from "lucide-react";
import type { Puppet } from "../../domain/types";

type Props = {
    puppets: Puppet[];
    selectedId: string | null;
    maxNameLength?: number;
    onSelect: (id: string) => void;
    onRename: (id: string, name: string) => void;
    onRotate: (id: string, delta: number) => void;
    onRemove: (id: string) => void;
    onEdit?: (id: string) => void;
};

const ROTATE_STEP = Math.PI / 8;

export default function PuppetList({
    puppets,
    selectedId,
    maxNameLength = 60,
    onSelect,
    onRename,
    onRotate,
    onRemove,
    onEdit,
}: Props) {
    const { t } = useTranslation();

    if (puppets.length === 0) {
        return (
            <p className="px-4 pb-4 text-xs text-content-tertiary">
                {t("constellation.list.empty")}
            </p>
        );
    }

    return (
        <div className="flex flex-col gap-2 px-4 pb-4" aria-label={t("constellation.list.title")}>
            <h3 className="text-sm font-semibold">
                {t("constellation.list.title")} ({puppets.length})
            </h3>
            <ul className="flex flex-col gap-2">
                {puppets.map((p) => {
                    const selected = p.id === selectedId;
                    return (
                        <li
                            key={p.id}
                            className={`flex items-center gap-1 rounded-lg border p-1.5 ${
                                selected ? "border-brand-400 bg-brand-50/50" : "border-gray-100"
                            }`}
                            onClick={() => onSelect(p.id)}
                        >
                            <input
                                type="text"
                                value={p.name}
                                maxLength={maxNameLength}
                                onChange={(e) => onRename(p.id, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                aria-label={t("constellation.list.rename")}
                                className="min-w-0 flex-1 rounded border-0 bg-transparent px-1 py-1 text-sm focus:bg-white focus:ring-1 focus:ring-brand-300"
                            />
                            {onEdit && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onEdit(p.id);
                                    }}
                                    title={t("constellation.list.edit")}
                                    aria-label={t("constellation.list.edit")}
                                    className="rounded p-2 text-gray-500 hover:text-brand-600"
                                >
                                    <Pencil className="h-4 w-4" />
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRotate(p.id, -ROTATE_STEP);
                                }}
                                title={t("constellation.patient.rotateLeft")}
                                aria-label={t("constellation.patient.rotateLeft")}
                                className="rounded p-2 text-gray-500 hover:text-brand-600"
                            >
                                <RotateCcw className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRotate(p.id, ROTATE_STEP);
                                }}
                                title={t("constellation.patient.rotateRight")}
                                aria-label={t("constellation.patient.rotateRight")}
                                className="rounded p-2 text-gray-500 hover:text-brand-600"
                            >
                                <RotateCw className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRemove(p.id);
                                }}
                                title={t("constellation.patient.remove")}
                                aria-label={t("constellation.patient.remove")}
                                className="rounded p-2 text-content-tertiary hover:text-red-600"
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
